import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { ColumnKeys, Dependencies } from '../types';
import { API_TYPE_EASM } from '../../../webcomponents.constants';
import { CategoriesConfig } from '../../../grid/genericGridSmartViews/types';
import { TemplateState } from '../../../types/common/types';
import { GetItemsResponse } from '../../../ui/gzSingleSelectComplex/types';
import { FilterChangeEvents } from '../../../grid/gridFilters/types';
import { CompanyFilterDashboardAsmDataProvider } from '../../../ui/gzSingleSelectComplex/dataProvider/company/CompanyFilterDashboardAsmDataProvider';
import { ConsoleRequest, DataProviderType } from '../../../utils/request';
import { CompanyEndpointMethod } from '../../../packages/packagesGrid/types';
import { ListItem } from '../../../ui/gzSingleSelectList/types';

export class EasmInventoryBaseGridView extends BaseWebComponentView {
  protected dependencies: Dependencies;
  protected smartViewsStrings: any;
  protected investigationStatusOptions: ListItem[];
  protected assigneeOptions: ListItem[];
  protected priorityOptions: ListItem[];
  private _openDetailsPanel = false;

  public get openDetailsPanel(): boolean {
    const openDetailsPanel = this._openDetailsPanel;

    this._openDetailsPanel = false;

    return openDetailsPanel;
  }

  getTranslations() {
    return {
      smartViewsStrings: this.smartViewsStrings,
    };
  }

  public getCompanyIdFilterValue(): string {
    if (this.dependencies.permissions.canHaveChildCompanies) {
      //In View, the first filter is set to be the company filter
      return this.dom.$gridView.filters[0].company.exactMatch;
    } else {
      return this.dependencies.getActiveCompany().companyId;
    }
  }

  public buildErrorView(error: Error): void {
    this.resetTemplateState(this.dom.$gridWrapper);

    this.setTemplateState(this.dom.$gridWrapper, TemplateState.ERROR);
  }

  protected async buildSmartViews(categoriesConfig: CategoriesConfig): Promise<void> {
    this.dom.$smartViews.categoriesConfig = categoriesConfig;
    this.dom.$smartViews.injectDependencies(this.dependencies);

    await this.dom.$smartViews.start();

    if (this.dependencies.navigationParams && this.dependencies.navigationParams.smartViewId) {
      this.dom.$smartViews.controller?.updateActiveViewById(this.dependencies.navigationParams.smartViewId);
    }

    if (this.dependencies.navigationParams && this.dependencies.navigationParams.smartViewLabel) {
      this.dom.$smartViews.controller?.updateActiveViewByLabel(this.dependencies.navigationParams.smartViewLabel);
    }

    this.setElementClass(this.dom.$smartViewsLoader, 'hidden');
    this.removeElementClass(this.dom.$smartViews, 'hidden');
  }

  protected getCompanyFilterUiConfig(columnKey): any {
    return {
      key: columnKey,
      config: {
        apiType: API_TYPE_EASM,
        populateFilterCallback: (filterComponent, value) => {
          const company = value.exactMatch;
          if (company) {
            const selectedValues: GetItemsResponse = {
              items: [],
              totalItemsCount: 0,
            };
            CompanyFilterDashboardAsmDataProvider.getCompanyNameById(company).then(companyName => {
              selectedValues.items.push({
                label: companyName,
                value: company,
              });
              selectedValues.totalItemsCount = 1;
              filterComponent.setFilterOptions(selectedValues).then(() => {
                filterComponent.value = value.exactMatch;
                const event = new CustomEvent(FilterChangeEvents.REMOTE_SELECT_CHANGED, {
                  detail: {
                    selectedValue: company,
                    label: companyName,
                    activeView: this.dom.$smartViews.controller?.getActiveView(),
                  },
                  bubbles: true,
                  composed: true,
                });
                filterComponent.dispatchEvent(event);
              });
            });
          }
        },
      },
    };
  }

  protected addPivotingFilterValues(): void {
    if (!this.dependencies.navigationParams) {
      return;
    }

    const existingFilters = this.dom.$gridView.filters;

    for (const filter of this.dependencies.navigationParams.filters) {
      let keyExists = false;
      const filterKey = Object.keys(filter)[0];
      const filterValue = filter[filterKey];

      if (filterKey === ColumnKeys.COMPANY && !this.dependencies.permissions.canHaveChildCompanies) {
        continue;
      }

      for (const [index, existingFilter] of existingFilters.entries()) {
        if (Object.keys(existingFilter)[0] === filterKey) {
          existingFilters[index][filterKey] = filterValue;
          keyExists = true;
        }
      }

      if (!keyExists) {
        existingFilters.push(filter);
      }
    }

    this.dom.$gridView.filters = existingFilters;

    this._openDetailsPanel = Boolean(this.dependencies.navigationParams.openDetailsPanel ?? false);

    this.dependencies.navigationParams = null;
  }

  public async hasCompaniesWithEASM(): Promise<boolean> {
    const response = await ConsoleRequest.createRequest<any>({
      dataProviderType: DataProviderType.ENDPOINTS,
      endpoint: 'Company',
      method: CompanyEndpointMethod.GET_AVAILABLE_COMPANIES,
      payload: {
        limit: 10,
        options: {
          source: 'fromEASM',
        },
      },
    });

    return response.count !== 0;
  }
}
